import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  setError,
  hideSurveyToast,
  getAnswers,
  saveSurveyPerson,
  clearSurveyResponse,
  clearSurveyError,
} from "app/features/surveySlice";
import { styled } from "@mui/material";
import { getToastMessage } from "lib/helpers/toastMessageHelper";
import FormCard from "components/Forms/FormCard/FormCard";
import Form from "./components/Form";
import NewToast from "ui/atoms/NewToast";
import { Answers, SurveyPerson, SurveyToken } from "types";
import ErrorBoundary from "components/ErrorBoundary/ErrorBoundary";
import { getTitle } from "./constants/formMessages";
import { surveyService } from "services/SurveyService";
import { personService } from "services/PersonService";
import isEmpty from "lodash/isEmpty";
import SurveyContent from "./components/SurveyContent";
import { useSection } from "lib/context/section";

const Survey = () => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const uid = searchParams.get("uid");
  const {
    surveyLoading,
    answers,
    surveySeverity,
    surveyResponse,
    surveyError,
    surveyShowToast,
    surveySection,
    isTokenExpired,
    loadingAnswers,
  } = useAppSelector((state) => state.survey) ?? [];

  const [answerComplement, setAnswerComplement] = useState<Answers>();
  const [tokenInfo, setTokenInfo] = useState<SurveyToken>();
  const [personInfo, setPersonInfo] = useState<SurveyPerson>();
  const [identificationNumber, setIdentificationNumber] = useState<string>();
  const isLoading = surveyLoading || loadingAnswers;
  const { sectionId, setSectionId } = useSection();

  /**
  Getting the token info from user Identificacion or query param token 
 */
  const getTokenInfo = () => {
    if (uid) {
      surveyService
        .authTokenId(uid as string)
        .then((response: SurveyToken) => {
          if (response.personId) setTokenInfo(response);
        })
        .catch((error) => {
          dispatch(setError(error.data));
        });
      return;
    }
    if (identificationNumber) {
      surveyService
        .getSurveyTokenId(identificationNumber as string)
        .then((response: SurveyToken) => {
          if (response.personId) setTokenInfo(response);
        })
        .catch((error) => {
          dispatch(setError(error.data));
        });
      return;
    }
  };

  /**
  Getting the user object based on the token info
 */
  const getPersonInfo = () => {
    const id = tokenInfo?.identificationNumber || identificationNumber;
    if (id && tokenInfo?.personId) {
      personService.getPerson({ identificationNumber: id, personId: tokenInfo?.personId }).then((response: any) => {
        if (response.personId) {
          setPersonInfo(response);

          if (isEmpty(answerComplement))
            setAnswerComplement({
              personID: tokenInfo?.personId as string,
              surveyTokenID: tokenInfo?.surveyTokenId as string,
            });
        }
      });
    }
  };

  const clearToast = () => {
    dispatch(hideSurveyToast());
    dispatch(clearSurveyResponse());
    dispatch(clearSurveyError());
  };

  useEffect(() => {
    if (isEmpty(tokenInfo)) {
      getTokenInfo();
    }
    if (!uid && !identificationNumber) {
      setSectionId(1);
    }
  }, [uid, identificationNumber]);

  useEffect(() => {
    if (isEmpty(personInfo)) {
      getPersonInfo();
    }
  }, [tokenInfo, identificationNumber, getTokenInfo]);

  useEffect(() => {
    if (tokenInfo?.personId && tokenInfo?.surveyTokenId) {
      dispatch(getAnswers({ personId: tokenInfo.personId, surveyTokenId: tokenInfo.surveyTokenId } as SurveyToken));
    }
  }, [sectionId]);

  useEffect(() => {
    return () => {
      clearToast();
    };
  }, []);

  const handleSubmit = (values: Answers[]) => dispatch(saveSurveyPerson({ person: values, sectionId }));

  return (
    <ErrorBoundary>
      <NewToast open={surveyShowToast} setOpen={clearToast} severity={surveySeverity}>
        {getToastMessage(surveyResponse, surveyError)}
      </NewToast>
      <SurveyWrapper>
        {sectionId <= 0 && !isEmpty(uid) && Boolean(String(uid)) ? (
          <SurveyContent
            disabled={isEmpty(personInfo)}
            tokenId={tokenInfo?.surveyTokenId}
            tokenError={surveyResponse?.errors?.surveyTokenId}
          />
        ) : (
          (sectionId > 0 && (
            <FormCard
              linearProgress={isLoading}
              maxWidthCard={775}
              maxWidthContainer='md'
              title={getTitle(surveySection?.module as string, sectionId)}
            >
              <Form
                answers={answers}
                loading={isLoading}
                handleSubmit={handleSubmit}
                isTokenExpired={isTokenExpired}
                tokenInfo={tokenInfo as SurveyToken}
                surveyPerson={personInfo as SurveyPerson}
                answerComplement={answerComplement as Answers}
                setIdentificationNumber={setIdentificationNumber}
              />
            </FormCard>
          )) ||
          null
        )}
      </SurveyWrapper>
    </ErrorBoundary>
  );
};

export default Survey;

const SurveyWrapper = styled("div")`
  display: flex;
  justify-content: center;
  align-items: center;
`;
